import { I_VACCINATION_STATUS } from '../../api/fetchVaccineSchedule';
import { IMilestoneHistory } from '../../components/common/types/milestones';

export type MHC = {
    patientMedicalConditions?: PatientMedicalCondition[];
    familyHistory?: FamilyHistory[];
    lifestyleHabits?: LifestyleHabits[];
    recentTravelHistory?: RecentTravelHistory[];
    currentMedications?: CurrentMedications[];
    drugAllergy?: DrugAllergy[];
    foodOtherAllergy?: FoodOthersAllergy[];
    pastProcedures?: PastProcedures[];
    otherMedicalHistory?: OtherMedicalHistory[];
};

export type MHCWithVaccination = MHC & {
    vaccinationHistory?: IVaccineStatus[];
};

export type MHCWithMilestone = MHC & {
    milestones?: IMilestoneHistory[];
};

export interface IVaccineStatus {
    id: string; // vaccine id
    status?: I_VACCINATION_STATUS;
    date?: string;
    brandId?: string;
    batchNumber?: string;
    notes?: string;
    diseaseId: string;
    doseNo: number;
    ageGroup?: string;
    name: string;
}

export type PatientMedicalCondition = {
    id: string;
    name: string;
    status?: string;
    reportedAt?: string;
    since?: {
        custom: string;
    };
    notes?: string;
};

export type FamilyHistory = {
    id: string;
    name: string;
    who?: string;
    reportedAt?: string;
    notes?: string;
    status?: string;
};

export type LifestyleHabits = {
    id: string;
    name: string;
    frequency?: {
        custom: string;
    };
    reportedAt?: string;
    since?: {
        custom: string;
    };
    notes?: string;
    status?: string;
};

export type RecentTravelHistory = {
    id: string;
    name: string;
    notes?: string;
    reportedAt?: string;
    status?: string;
};

export type CurrentMedications = {
    id: string;
    name: string;
    reportedAt?: string;
    frequency?: {
        custom: string;
    };
    since?: {
        custom: string;
    };
    notes?: string;
    status?: string;
};

export type DrugAllergy = {
    id: string;
    name: string;
    reportedAt?: string;
    since?: {
        custom: string;
    };
    notes?: string;
    status?: string;
    generic_id?: string;
    generic_name?: string;
};

export type FoodOthersAllergy = {
    id: string;
    name: string;
    reportedAt?: string;
    since?: {
        custom: string;
    };
    notes?: string;
    status?: string;
};

export type PastProcedures = {
    id: string;
    name: string;
    reportedAt?: string;
    on?: {
        mm: string;
        yyyy: string;
    };
    notes?: string;
    status?: string;
};

export type OtherMedicalHistory = {
    id: string;
    name: string;
    reportedAt?: string;
    notes?: string;
    status?: string;
};

export enum MEDICAL_HISTORY_CATEGORY {
    PATIENT_MEDICAL_CONDITIONS = 'patientMedicalConditions',
    FAMILY_HISTORY = 'familyHistory',
    LIFESTYLE_HABITS = 'lifestyleHabits',
    RECENT_TRAVEL_HISTORY = 'recentTravelHistory',
    CURRENT_MEDICATIONS = 'currentMedications',
    DRUG_ALLERGY = 'drugAllergy',
    FOOD_OTHER_ALLERGY = 'foodOtherAllergy',
    PAST_PROCEDURES = 'pastProcedures',
    OTHER_MEDICAL_HISTORY = 'otherMedicalHistory',
}

interface MCI extends PatientMedicalCondition {
    catId?: typeof MEDICAL_HISTORY_CATEGORY.PATIENT_MEDICAL_CONDITIONS;
}
interface FHI extends FamilyHistory {
    catId?: typeof MEDICAL_HISTORY_CATEGORY.FAMILY_HISTORY;
}
interface LSHI extends LifestyleHabits {
    catId?: typeof MEDICAL_HISTORY_CATEGORY.LIFESTYLE_HABITS;
}
interface RTHI extends RecentTravelHistory {
    catId?: typeof MEDICAL_HISTORY_CATEGORY.RECENT_TRAVEL_HISTORY;
}
interface CMI extends CurrentMedications {
    catId?: typeof MEDICAL_HISTORY_CATEGORY.CURRENT_MEDICATIONS;
}
interface DAI extends DrugAllergy {
    catId?: typeof MEDICAL_HISTORY_CATEGORY.DRUG_ALLERGY;
}
interface FOAI extends FoodOthersAllergy {
    catId?: typeof MEDICAL_HISTORY_CATEGORY.FOOD_OTHER_ALLERGY;
}
interface PPI extends PastProcedures {
    catId?: typeof MEDICAL_HISTORY_CATEGORY.PAST_PROCEDURES;
}
interface OMHI extends OtherMedicalHistory {
    catId?: typeof MEDICAL_HISTORY_CATEGORY.OTHER_MEDICAL_HISTORY;
}

export type MHItem = MCI | FHI | LSHI | RTHI | CMI | DAI | FOAI | PPI | OMHI;
export type MHSince =
    | PatientMedicalCondition
    | LifestyleHabits
    | CurrentMedications
    | DrugAllergy
    | FoodOthersAllergy;

export type MHFrequency = LifestyleHabits | CurrentMedications;

export type MHItem2 =
    | PatientMedicalCondition
    | FamilyHistory
    | LifestyleHabits
    | RecentTravelHistory
    | CurrentMedications
    | DrugAllergy
    | FoodOthersAllergy
    | PastProcedures
    | OtherMedicalHistory
    | IVaccineStatus;
